import { z } from "zod";

/**
 * What a step runs. A recipe is a name from a closed catalogue, never a command: the engine maps
 * each one to code it already trusts, so a flow cannot make the runner execute anything else.
 *
 * - `auto` completes inside the engine (a note, a plan).
 * - `agent` starts a sandboxed agent task through the control plane.
 * - `runner` is a job on the flow runner, outside the sandbox where packages and images can be
 *   fetched and built.
 */
export const recipeExecutors = ["auto", "agent", "runner"] as const;
export const recipeExecutorSchema = z.enum(recipeExecutors);
export type RecipeExecutor = z.infer<typeof recipeExecutorSchema>;

export interface FlowRecipe {
  id: string;
  executor: RecipeExecutor;
  label: string;
  summary: string;
}

export const flowRecipes: readonly FlowRecipe[] = [
  { id: "auto.note", executor: "auto", label: "Note", summary: "Records the step and continues." },
  {
    id: "auto.plan",
    executor: "auto",
    label: "Plan",
    summary: "Records the run's intent as the plan and continues.",
  },
  {
    id: "agent.work",
    executor: "agent",
    label: "Agent work",
    summary: "An agent does the run's intent on the run's branch.",
  },
  {
    id: "agent.review",
    executor: "agent",
    label: "Agent review",
    summary: "An agent reads the branch and reports; it changes nothing.",
  },
  {
    id: "repo.diff",
    executor: "runner",
    label: "Read the diff",
    summary: "Lists what the branch changes against main.",
  },
  {
    id: "repo.scan-secrets",
    executor: "runner",
    label: "Scan for secrets",
    summary: "Looks for key and token patterns; reports file and line, never the value.",
  },
  {
    id: "node.install",
    executor: "runner",
    label: "Install dependencies",
    summary: "pnpm install --frozen-lockfile in a throwaway container.",
  },
  {
    id: "node.typecheck",
    executor: "runner",
    label: "Typecheck",
    summary: "pnpm typecheck with no network.",
  },
  {
    id: "node.test",
    executor: "runner",
    label: "Unit tests",
    summary: "pnpm test with no network.",
  },
  {
    id: "node.build",
    executor: "runner",
    label: "Production build",
    summary: "pnpm build with no network.",
  },
  {
    id: "node.audit",
    executor: "runner",
    label: "Audit dependencies",
    summary: "pnpm audit --prod; high and critical advisories fail the step.",
  },
  {
    id: "node.licences",
    executor: "runner",
    label: "Check licences",
    summary: "Lists production dependency licences and flags non-permissive ones.",
  },
  {
    id: "image.build",
    executor: "runner",
    label: "Build the image",
    summary: "docker build for linux/amd64, tagged with the commit.",
  },
  {
    id: "image.smoke",
    executor: "runner",
    label: "Smoke-test the image",
    summary: "Runs the image and checks /healthz and the security headers.",
  },
  {
    id: "image.push",
    executor: "runner",
    label: "Push the image",
    summary: "Pushes the image to the registry.",
  },
  {
    id: "deploy.roll",
    executor: "runner",
    label: "Roll out",
    summary: "Rolls the landing service to the image tag on sg-cloud, with a backup.",
  },
  {
    id: "deploy.rollback",
    executor: "runner",
    label: "Roll back",
    summary: "Restores the previous landing tag on sg-cloud.",
  },
  {
    id: "probe.http",
    executor: "runner",
    label: "Check reachability",
    summary: "Requests / and /healthz and records status and time.",
  },
  {
    id: "probe.headers",
    executor: "runner",
    label: "Check headers",
    summary: "Confirms the security headers on every route.",
  },
  {
    id: "probe.tls",
    executor: "runner",
    label: "Check the certificate",
    summary: "Reports the certificate expiry, or that the page is not public yet.",
  },
  {
    id: "probe.logs",
    executor: "runner",
    label: "Read recent logs",
    summary: "Reads the service's recent log lines and host disk and memory.",
  },
] as const;

const byId = new Map(flowRecipes.map((recipe) => [recipe.id, recipe]));

export function findRecipe(id: string | null | undefined): FlowRecipe | null {
  return id ? (byId.get(id) ?? null) : null;
}

export const flowRecipeIdSchema = z.string().refine((id) => byId.has(id), "Unknown recipe.");
